import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce, next, schedule, throttle } from '@ember/runloop';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { BlockThemeConfig } from 'webapp-types';
import { BlockRenderStep, PaginationDirection } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import horizontalScroll from 'webapp/modifiers/horizontal-scroll';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockContentNumberAndChartComponent from 'webapp/components/block/content/number-and-chart';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import Visualization from 'webapp/models-custom/visualization/base';

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	themeData: BlockThemeConfig.BlockParsed;
}

const minNumberSizeInBlockSizeUnit = 2; // e.g. block with width 4 or 5 can show 2 numbers in a row, and block with width 3 only 1
const minNumberSizeInBlockSizeUnitForCompareOnRight = 5;

export default class BlockContentTopNumbersComponent extends Component<Args> {
	node: HTMLElement | null = null;
	innerNode: HTMLElement | null = null;
	gridCellWidth = 0;

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get showNumbers() {
		return this.numberAndChartPairs.length > 0;
	}

	get isSingle() {
		return this.numberAndChartPairs.length === 1;
	}

	get showTitles() {
		return this.numberAndChartPairs.length > 1;
	}

	@cached
	get isCompareOnRight() {
		// only bar/line,none of numbers may have more than 1 compare point
		// and must have space for the compare
		return (
			(this.visualization.isLineChart || this.visualization.isBarChart) &&
			!this.numberAndChartPairs.some((pair) => pair.numberPoint.comparePoints.length > 1) &&
			(this.isSingle || this.hasSpaceForCompareOnRight)
		);
	}

	@cached
	get hasSpaceForCompareOnRight() {
		const { columnCount, visibleColumnCount } = this.getGridLayout();
		return (
			visibleColumnCount >= columnCount &&
			this.block.sizes.width / columnCount >= minNumberSizeInBlockSizeUnitForCompareOnRight
		);
	}

	get useNumberGrid() {
		const isNumberOnMobile = this.visualization.isNumber && !this.args.blockRenderer.isWallView;
		return !isNumberOnMobile;
	}

	@cached
	get numberAndChartPairs() {
		let pairs;
		if (Visualization.isNumber(this.visualization)) {
			pairs = this.visualization.numberPointAndChartOutputPairs;
		} else {
			pairs = this.visualization.numberPoints.map((numberPoint) => {
				return { numberPoint };
			});
		}
		return pairs;
	}

	@action
	prePrepareNumberGrid(singleNumberNode: HTMLElement) {
		// called from number-and-chart components on first render (see comment in their setupView)
		const node = singleNumberNode.closest<HTMLElement>('.bc__top-numbers')!,
			innerNode = node.getElementsByClassName('bc_top-numbers-inner')[0] as HTMLElement;
		this.prepareNumberGrid(node, innerNode);
	}

	private prepareNumberGrid(node = this.node, innerNode = this.innerNode) {
		if (!this.useNumberGrid) {
			return;
		}

		if (!this.showNumbers) {
			return;
		}

		if (!node) {
			return;
		}

		// not waiting for the next runloop (it's either this or afterRender in PRE_NUMBERS)
		node.classList.remove('hidden');

		if (!innerNode) {
			return;
		}

		const availableHeight = this.getHeightForNumbers(),
			gridNode = node.getElementsByClassName('bc_top-numbers-grid')[0] as HTMLElement;

		if (!gridNode) {
			return;
		}

		innerNode.style.maxHeight = `${availableHeight}px`;
		if (this.isSingle) {
			// we center single, so skip this
			innerNode.style.height = 'auto';
			gridNode.style.width = 'auto';
			return;
		}

		const { rowCount, columnCount, visibleColumnCount } = this.getGridLayout(),
			singleCellWidth = Math.floor(
				this.args.blockRenderer.blockInnerWidth / Math.min(columnCount, visibleColumnCount),
			),
			visibleWidth = columnCount * singleCellWidth;

		innerNode.style.height = `${availableHeight}px`;
		gridNode.style.width = `${visibleWidth}px`;
		gridNode.style.gridTemplateRows = `repeat(${rowCount}, 1fr)`;
		gridNode.style.gridTemplateColumns = `repeat(${columnCount}, 1fr)`;

		this.gridCellWidth = singleCellWidth; // used when scrolling with arrows

		next(() => {
			schedule('afterRender', this, this.updateGridScrollArrows);
		});
	}

	private getGridLayout() {
		const itemsToRenderCount = this.numberAndChartPairs.length,
			canHaveMultipleRows = this.visualization.isNumber; // only number can have multiple rows

		let rowCount = 1;
		if (canHaveMultipleRows) {
			// check if we could squeeze it all in one row
			// if we have extra width for each box (1.5*1.3 = roughly 2 blocks)
			const allFitsInOneRow =
				this.block.sizes.width / (minNumberSizeInBlockSizeUnit * 1.3) >= itemsToRenderCount;
			if (!allFitsInOneRow) {
				rowCount = Math.max(
					1,
					Math.floor(this.block.sizes.height / minNumberSizeInBlockSizeUnit),
				);
			}
		}

		const columnCount = Math.ceil(itemsToRenderCount / rowCount);
		const visibleColumnCount = Math.max(
			1,
			Math.floor(this.block.sizes.width / minNumberSizeInBlockSizeUnit),
		);

		if (canHaveMultipleRows) {
			// if all columns are visible, check if we can optimize division in rows
			// e.g. dont create 2x4 grid if we only have 6 items and 2x3 would fit nicer
			if (visibleColumnCount >= columnCount) {
				if (itemsToRenderCount / columnCount < rowCount) {
					rowCount = Math.ceil(itemsToRenderCount / columnCount);
				}
			}
		}

		return { rowCount, columnCount, visibleColumnCount };
	}

	private updateGridScrollArrows() {
		if (!isAlive(this)) {
			return;
		}

		if (!this.useNumberGrid) {
			return;
		}

		const innerNode = this.innerNode;

		if (!innerNode) {
			return;
		}

		const innerNodeWidth = innerNode.clientWidth,
			scrollOverflow = innerNode.scrollWidth - innerNodeWidth;

		const scrollLeft = Math.round(innerNode.scrollLeft);

		innerNode.classList.toggle('has-arrow--prev', scrollLeft > 1); // allow error by 1px
		innerNode.classList.toggle(
			'has-arrow--next',
			scrollLeft + innerNodeWidth < innerNodeWidth + scrollOverflow,
		);
	}

	private getHeightForNumbers() {
		let availableHeight = this.args.blockRenderer.getHeightForNumbersAndContent();
		if (!this.visualization.isNumber) {
			// smaller space for number if it's single and the compare will be shown to the right
			if (this.isSingle && this.isCompareOnRight) {
				// 20% of height, max 6em
				availableHeight = availableHeight / 5;
				const minimumInPx = this.args.blockRenderer.blockFontSize * 6;
				availableHeight = Math.min(minimumInPx, availableHeight);
			} else {
				// chart 3 times higher than number
				availableHeight = availableHeight / 4;
			}
		}
		return availableHeight;
	}

	private scrollInDirection(direction: PaginationDirection) {
		const innerNode = this.innerNode;

		if (!innerNode) {
			return;
		}

		const scrollLeft = innerNode.scrollLeft;

		const scrollTo =
			direction === PaginationDirection.PREVIOUS
				? Math.max(0, scrollLeft - innerNode.clientWidth)
				: scrollLeft + innerNode.clientWidth;

		innerNode.scrollTo(scrollTo, 0);

		this.afterScroll();
	}

	@action
	afterScroll(): void {
		// how long is the scroll animation?
		debounce(this, this.updateGridScrollArrows, 500);
	}

	@action
	useArrow(e: PointerEvent) {
		const target = e.target as HTMLElement;
		if (target) {
			const direction = target.classList.contains('arrow--prev')
				? PaginationDirection.PREVIOUS
				: PaginationDirection.NEXT;
			throttle(this, this.scrollInDirection, direction, 100);
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
		this.innerNode = node.getElementsByClassName('bc_top-numbers-inner')[0] as HTMLElement;

		this.prepareNumberGrid();

		const blockRenderer = this.args.blockRenderer;

		blockRenderer.registerStepHandler(BlockRenderStep.PRE_NUMBERS, this, () => {
			this.prepareNumberGrid();
			return Promise.resolve();
		});

		blockRenderer.registerStepHandler(BlockRenderStep.POST_NUMBERS, this, (changes) => {
			// if we want to show numbers, and we have data for them, but numbersHeight is not yet set,
			// we need to delay resolution of this until the actual number nodes have rendered
			// by waiting, we reduce calls to updateNumbersHeight and do not unnecessarily call resizeChart on empty charts
			if (
				!blockRenderer.numbersHeight &&
				this.showNumbers &&
				this.numberAndChartPairs.length
			) {
				return new Promise((resolve) => {
					// scheduling the delay
					schedule('afterRender', this, () => {
						next(() => {
							const newChanges = blockRenderer.updateNumbersHeight();
							if (newChanges) {
								changes.numbersHeight = newChanges;
							}
							resolve();
						});
					});
				});
			}

			// no delay needed, update immediately
			const newChanges = blockRenderer.updateNumbersHeight();
			if (newChanges) {
				changes.numbersHeight = newChanges;
			}
			return Promise.resolve();
		});

		blockRenderer.updateNumbersHeight();

		this.innerNode.addEventListener('scroll', this.afterScroll);

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.PRE_NUMBERS, this);
			blockRenderer.unregisterStepHandler(BlockRenderStep.POST_NUMBERS, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
		this.innerNode?.removeEventListener('scroll', this.afterScroll);
	}
	<template>
		<div
			class="bc__top-numbers bc__top-numbers--{{if this.isSingle 'single' 'multiple'}}
				{{unless this.showNumbers 'hidden'}}"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<div class="bc_top-numbers-inner" {{horizontalScroll 40}}>
				<a class="arrow arrow--prev" {{on "click" this.useArrow}}>&lt;</a>
				<div class="bc_top-numbers-grid">
					{{#each this.numberAndChartPairs as |numberAndChartPair|}}
						<BlockContentNumberAndChartComponent
							@model={{numberAndChartPair}}
							@blockState={{@blockState}}
							@blockRenderer={{@blockRenderer}}
							@themeData={{@themeData}}
							@isCompareOnRight={{this.isCompareOnRight}}
							@showTitle={{this.showTitles}}
							@scaleVertically={{this.useNumberGrid}}
							@prePrepareNumberGrid={{this.prePrepareNumberGrid}}
						/>
					{{/each}}
				</div>
				<a class="arrow arrow--next" {{on "click" this.useArrow}}>&gt;</a>
			</div>
		</div>
	</template>
}
